import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";
import type { CreateCompetition } from "~pages/Competitions/types/upsertCompetition.ts";
import type { Competition, CompetitionResponse } from "~types/competition.ts";

export const useCreateCompetition = () => {
  const { sport, organizationId } = useParams();
  const request = useApi();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation<Competition, Error, CreateCompetition>({
    mutationFn: async (data) => {
      const response = await request<CompetitionResponse>(`${sport}/o/${organizationId}/competitions`, {
        method: "POST",
        body: data,
      });

      return response?.data?.[0];
    },
    onSuccess: async (createdCompetition) => {
      if (!createdCompetition?.competitionId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        if (updatedScope.resources.competitions) {
          updatedScope.resources.competitions[createdCompetition.competitionId] = createdCompetition;
        } else {
          updatedScope.resources.competitions = {
            [createdCompetition.competitionId]: createdCompetition,
          };
        }

        const leagueToUpdate = updatedScope.scope.leagueTree.find(({ leagueId }) => leagueId === createdCompetition.leagueId);
        if (leagueToUpdate) {
          const existsInTree = leagueToUpdate.competitions.some(({ competitionId: id }) => id === createdCompetition.competitionId);
          if (!existsInTree) {
            leagueToUpdate.competitions.push({ competitionId: createdCompetition.competitionId, seasonIds: [] });
          }
        }
      });

      await invalidateCache();
    },
  });
};
